import { request } from "@/lib/apiClient";
import { getApiBaseUrl } from "@/lib/apiBaseUrl";

interface FyersConnectionRequest {
  client_id: string;
  app_id: string;
  app_secret: string;
  redirect_url: string;
  app_type: string;
  // Auto-population fields
  firebase_uid?: string;
  email?: string;
  // Optional fields for future use
  totp_key?: string;
  pin?: string;
  name?: string;
  phone?: number;
  app_id_type?: string;
}

interface FyersConnectionResponse {
  client_id: string;
  status: string;
  message: string;
  redirect_url?: string;
  is_new_user: boolean;
}

interface FyersUserResponse {
  client_id: string;
  name: string;
  phone: number;
  app_type: string;
  redirect_url: string;
  app_id_type: string;
  status: string;
  created_at: string;
  available_balance: number;
}

interface SessionTokenResponse {
  client_id: string;
  status: string;
  message: string;
  session_token?: string;
  is_existing_user: boolean;
}

interface ConnectionStatusResponse {
  client_id: string;
  status: string;
  message: string;
  is_connected: boolean;
  created_at?: string;
  available_balance?: number;
  total_investment?: number;
  total_pnl?: number;
  total_pnl_percentage?: number;
  day_pnl?: number;
  day_pnl_percentage?: number;
  monthly_returns?: Array<{
    month: string;
    profit: number;
    returnPercentage: number;
    status: string;
  }>;
  last_updated?: string;
}

interface PortfolioDataResponse {
  status: string;
  message: string;
  data?: {
    available_balance: number;
    total_investment: number;
    total_pnl: number;
    total_pnl_percentage: number;
    day_pnl: number;
    day_pnl_percentage: number;
    positions: unknown[];
    last_updated: string;
  };
}

interface FyersProfileResponse {
  s: string;
  code: number;
  message: string;
  data: {
    name: string;
    image: string;
    display_name: string;
    email_id: string;
    PAN: string;
    fy_id: string;
    pin_change_date: string;
    mobile_number: string;
    totp: boolean;
    pwd_change_date: string;
    pwd_to_expire: number;
    ddpi_enabled: boolean;
    mtf_enabled: boolean;
  };
}

interface MonthlyReturnsResponse {
  client_id: string;
  status: string;
  data: Array<{
    month: string;
    profit: number;
    returnPercentage: number;
    status: string;
  }>;
}

class FyersService {
  private apiCall<T>(
    endpoint: string,
    options: { method?: string; body?: unknown } = {}
  ): Promise<T> {
    const { method = "GET", body } = options;
    return request<T>(endpoint, {
      method,
      body,
    });
  }

  /**
   * Connect Fyers account
   */
  async connectFyersAccount(data: FyersConnectionRequest): Promise<FyersConnectionResponse> {
    return this.apiCall<FyersConnectionResponse>('/fyers/connect', {
      method: 'POST',
      body: data,
    });
  }

  /**
   * Generate session token
   */
  async generateSessionToken(clientId: string, authCode?: string): Promise<SessionTokenResponse> {
    const url = authCode
      ? `/fyers/generate-session/${clientId}?auth_code=${authCode}`
      : `/fyers/generate-session/${clientId}`;

    return this.apiCall<SessionTokenResponse>(url, {
      method: 'POST',
    });
  }

  /**
   * Verify user activation status
   */
  async verifyUserActivation(clientId: string): Promise<{
    client_id: string;
    is_activated: boolean;
    status: string;
    message: string;
  }> {
    return this.apiCall<{
      client_id: string;
      is_activated: boolean;
      status: string;
      message: string;
    }>(`/fyers/verify-activation/${clientId}`, {
      method: 'POST',
    });
  }

  /**
   * Update user status after OAuth completion
   */
  async updateUserStatus(clientId: string, accessToken?: string): Promise<{
    success: boolean;
    message: string;
  }> {
    const url = accessToken
      ? `/fyers/update-user-status/${clientId}?access_token=${accessToken}`
      : `/fyers/update-user-status/${clientId}`;

    return this.apiCall<{
      success: boolean;
      message: string;
    }>(url, {
      method: 'POST',
    });
  }

  /**
   * Get user details
   */
  async getFyersUser(clientId: string): Promise<FyersUserResponse> {
    return this.apiCall<FyersUserResponse>(`/fyers/user/${clientId}`);
  }

  /**
   * Update user details
   */
  async updateFyersUser(clientId: string, data: Partial<FyersConnectionRequest>): Promise<FyersUserResponse> {
    return this.apiCall<FyersUserResponse>(`/fyers/update/${clientId}`, {
      method: 'PATCH',
      body: data,
    });
  }

  /**
   * Fetch Fyers profile (Proxy through backend)
   */
  async getFyersProfile(clientId: string): Promise<FyersProfileResponse> {
    return this.apiCall<FyersProfileResponse>(`/fyers/profile/${clientId}`);
  }

  /**
   * Get connection status
   */
  async getConnectionStatus(clientId: string): Promise<ConnectionStatusResponse> {
    return this.apiCall<ConnectionStatusResponse>(`/fyers/status/${clientId}`);
  }

  /**
   * Handle OAuth callback
   */
  async handleOAuthCallback(clientId: string, authCode: string, state?: string): Promise<{ message: string; client_id: string; status: string; session_token?: string }> {
    const url = state
      ? `/fyers/callback/${clientId}?auth_code=${authCode}&state=${state}`
      : `/fyers/callback/${clientId}?auth_code=${authCode}`;

    return this.apiCall(url, {
      method: 'POST',
    });
  }

  /**
   * Get portfolio data
   */
  async getPortfolioData(clientId: string): Promise<PortfolioDataResponse> {
    return this.apiCall<PortfolioDataResponse>(`/fyers/portfolio/${clientId}`, {
      method: 'GET',
    });
  }

  /**
   * Get monthly returns
   */
  async getMonthlyReturns(clientId: string): Promise<MonthlyReturnsResponse> {
    return this.apiCall<MonthlyReturnsResponse>(`/fyers/monthly-returns/${clientId}`, {
      method: 'GET',
    });
  }

  /**
   * Check if user has Fyers credentials by Firebase UID
   */
  async checkUserCredentials(firebaseUid: string): Promise<{
    hasCredentials: boolean;
    client_id?: string;
    message?: string;
    isApiError?: boolean;
  }> {
    // Caching/dedup is handled by TanStack Query (see features/fyers/fyersCredentials).
    try {
      const response = await this.apiCall<{
        success: boolean;
        message: string;
        firebase_uid: string;
        fyers_user?: {
          client_id: string;
          name: string;
          phone: number;
          status: string;
          created_at: string;
          available_balance: number;
        } | null;
      }>(`/fyers/user/${firebaseUid}?minimal=1`);

      const hasCredentials = response.success && !!response.fyers_user?.client_id;

      return {
        hasCredentials,
        client_id: response.fyers_user?.client_id,
        message: response.message,
        isApiError: false,
      };
    } catch (error) {
      // Distinguish a real API/network error from a legitimate "not found".
      const isNetworkError = error instanceof Error && (
        error.message.includes('fetch') ||
        error.message.includes('Network') ||
        error.message.includes('Failed to fetch') ||
        error.message.includes('ERR_NETWORK') ||
        error.message.includes('500') ||
        error.message.includes('502') ||
        error.message.includes('503')
      );

      if (isNetworkError) {
        return {
          hasCredentials: false,
          message: 'API connection failed',
          isApiError: true,
        };
      }

      return {
        hasCredentials: false,
        message: 'No Fyers connection found',
        isApiError: false,
      };
    }
  }

  /**
   * Check if user exists in Fyers by phone number
   */
  async checkUserByPhone(phoneNumber: string): Promise<{
    userExists: boolean;
    client_id?: string;
    firebase_uid?: string;
    message?: string;
    isApiError?: boolean;
  }> {
    try {
      console.log('Checking Fyers user by phone number:', phoneNumber);

      const response = await this.apiCall<{
        success: boolean;
        message: string;
        fyers_user?: {
          client_id: string;
          name: string;
          phone: number;
          firebase_uid?: string;
          status: string;
          created_at: string;
          available_balance: number;
        } | null;
      }>(`/fyers/check-phone/${phoneNumber}`);

      console.log('Fyers phone check response:', response);

      const userExists = response.success && !!response.fyers_user;
      console.log('User exists in Fyers:', userExists);

      return {
        userExists,
        client_id: response.fyers_user?.client_id,
        firebase_uid: response.fyers_user?.firebase_uid,
        message: response.message,
        isApiError: false
      };
    } catch (error) {
      console.error('Error checking Fyers user by phone:', error);

      // Check if it's a network/API error vs a legitimate "not found" response
      const isNetworkError = error instanceof Error && (
        error.message.includes('fetch') ||
        error.message.includes('Network') ||
        error.message.includes('Failed to fetch') ||
        error.message.includes('ERR_NETWORK') ||
        error.message.includes('500') ||
        error.message.includes('502') ||
        error.message.includes('503')
      );

      if (isNetworkError) {
        console.error('Network/API error detected, treating as API unavailable');
        return {
          userExists: false,
          message: 'API connection failed',
          isApiError: true
        };
      }

      // For other errors (like 404 not found), treat as user not found
      return {
        userExists: false,
        message: 'User not found in Fyers',
        isApiError: false
      };
    }
  }

  /**
   * Test connection (for development)
   */
  async testConnection(): Promise<{ status: string; message: string }> {
    try {
      const baseUrl = getApiBaseUrl();
      const healthUrl = baseUrl.replace('/api/v1', '');
      const response = await fetch(`${healthUrl}/health`);
      if (response.ok) {
        return { status: 'success', message: 'Backend is accessible' };
      } else {
        return { status: 'error', message: 'Backend responded with error' };
      }
    } catch (error) {
      return { status: 'error', message: 'Cannot connect to backend' };
    }
  }
  /**
   * Fetch an available unused proxy
   */
  async getAvailableProxy(firebaseUid?: string, broker?: string): Promise<{ success: boolean; data?: { proxy_static_ip: string; is_locked?: boolean }; message?: string }> {
    const params = new URLSearchParams();
    if (firebaseUid) params.append('firebase_uid', firebaseUid);
    if (broker) params.append('broker', broker);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return this.apiCall(`/proxies/available${queryString}`, {
      method: 'GET',
    });
  }

  /**
   * Assign a proxy to a client
   */
  async assignProxy(clientId: string, proxyStaticIp: string): Promise<{ success: boolean; message: string; data?: unknown }> {
    return this.apiCall(`/proxies/assign/${clientId}`, {
      method: 'POST',
      body: { proxy_static_ip: proxyStaticIp },
    });
  }
}

export const fyersService = new FyersService();
export type {
  FyersConnectionRequest,
  FyersConnectionResponse,
  FyersUserResponse,
  SessionTokenResponse,
  ConnectionStatusResponse,
  PortfolioDataResponse,
  MonthlyReturnsResponse,
  FyersProfileResponse
};
